<script>
import { mapGetters, mapActions } from 'vuex'
import draggable from 'vuedraggable'
import {
  SelectEducatorsDragGroup,
  EducatorListStates,
} from '@/shared/constants/booking/BookingSelectEducators'
import Spinner from '@/components/general/spinner.vue'
import CandidateDetailsModal from '@/components/candidate/detailsPopup/candidate-details-modal.vue'
import BookableEducatorCard from './bookable-educator-card.vue'

const {
  removeFromQueue,
  availableEducators,
  preferredQueue,
} = SelectEducatorsDragGroup

const { Loading, Empty, Error, Exhausted, List } = EducatorListStates

export default {
  name: 'BookableEducatorList',
  components: {
    BookableEducatorCard,
    CandidateDetailsModal,
    draggable,
    Spinner,
  },
  props: {
    bookingDate: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      removalSink: [],
      returningEducatorId: null,
      isListExhaustionPending: false,
      selectedCandidateId: null,
      showCandidateModal: false,
      removeGroup: {
        name: removeFromQueue,
        pull: false,
        put: [preferredQueue],
      },
      availableEducatorsGroup: {
        name: availableEducators,
        pull: 'clone',
        put: false,
      },
      ...EducatorListStates,
    }
  },
  computed: {
    ...mapGetters({
      preferredEducatorIds: 'bookingCreate/preferredEducatorIds',
      invalidEducatorIds: 'bookingCreate/invalidEducatorIds',
      isQueueFull: 'bookingCreate/isQueueFull',
      showRemoveOverlay: 'bookingCreate/isPreferredQueueDragging',
      bookableEducators: 'bookingCreate/bookableEducators',
      isLoading: 'bookingCreate/isBookableEducatorsLoading',
      bookingTimeZone: 'bookingCreate/bookingTimeZone',
      isSendToANZUK: 'bookings/isSendToANZUK',
    }),
    educators() {
      return this.bookableEducators.candidates
    },
    hasError() {
      return this.bookableEducators.hasError
    },
    visibleEducators() {
      return this.educators.filter(({ id }) => {
        const isInvalid = this.invalidEducatorIds.includes(id)
        const isQueued = this.preferredEducatorIds.includes(id)
        const isReturningDuringDrag =
          this.showRemoveOverlay && id === this.returningEducatorId

        return !isInvalid && !isQueued && !isReturningDuringDrag
      })
    },
    contentState() {
      if (this.hasError) return Error
      if (this.isLoading) return Loading
      if (!this.educators.length) return Empty
      if (!this.visibleEducators.length) return Exhausted

      return List
    },
    displayedContentState() {
      return this.isListExhaustionPending ? List : this.contentState
    },
    isAvailableDragDisabled() {
      return (
        this.isQueueFull ||
        this.hasError ||
        this.isLoading ||
        this.isSendToANZUK
      )
    },
    showPagination() {
      return (
        this.educators?.length &&
        !this.hasError &&
        this.bookableEducators.totalPages > 1 &&
        !this.isLoading
      )
    },
    searchTerm: {
      get() {
        return this.bookableEducators.searchTerm
      },
      set(searchTerm) {
        this.setBookableEducatorSearchTerm(searchTerm)
      },
    },
    sendToANZUK: {
      get() {
        return this.isSendToANZUK
      },
      set(value) {
        this.updateSendToANZUK(value)
      },
    },
  },
  watch: {
    contentState(currentState, previousState) {
      const isListBecomingExhausted =
        previousState === List && currentState === Exhausted

      if (isListBecomingExhausted) {
        this.isListExhaustionPending = true
        return
      }

      this.isListExhaustionPending = false
    },
    preferredEducatorIds(currentIds, previousIds) {
      this.returningEducatorId =
        previousIds.find((id) => !currentIds.includes(id)) ?? null
    },
    showRemoveOverlay(isDragging) {
      if (isDragging) this.returningEducatorId = null
    },
  },
  methods: {
    ...mapActions({
      fetchBookableEducators: 'bookingCreate/fetchBookableEducators',
      searchBookableEducators: 'bookingCreate/searchBookableEducators',
      changeBookableEducatorPage: 'bookingCreate/changeBookableEducatorPage',
      setBookableEducatorSearchTerm:
        'bookingCreate/setBookableEducatorSearchTerm',
      addEducator: 'bookingCreate/addEducator',
      removeFromQueue: 'bookingCreate/removeFromQueue',
      updateSendToANZUK: 'bookings/updateSendToANZUK',
    }),
    clearSearch() {
      this.searchTerm = ''
      this.searchBookableEducators()
    },
    onRemoveTargetChange(event) {
      if (!event.added) return

      this.removeFromQueue(event.added.element.id)
      this.removalSink = []
    },
    onEducatorEnterComplete() {
      this.returningEducatorId = null
    },
    onEducatorLeaveComplete() {
      if (this.contentState !== Exhausted) return

      this.isListExhaustionPending = false
    },
    showCandidateDialog(candidateId) {
      this.selectedCandidateId = candidateId
      this.showCandidateModal = true
    },
    closeCandidateDialog() {
      this.selectedCandidateId = null
      this.showCandidateModal = false
    },
  },
}
</script>

<template>
  <v-card
    class="fill-height d-flex flex-column p-lg rounded-lg"
    data-testid="bookable-educator-list"
  >
    <div class="mb-lg flex-shrink-0">
      <h4 class="display-xs mb-1">
        {{ $t('bookingCreateV2.selectEducatorsStep.availableTitle') }}
      </h4>
      <p class="text-xs-regular neutrals-grey-500-text mb-0">
        {{ $t('bookingCreateV2.selectEducatorsStep.availableSubtitle') }}
      </p>
    </div>

    <div class="d-flex align-start mb-lg">
      <v-switch
        id="create-booking-form-v2__send-to-anzuk-checkbox"
        v-model="sendToANZUK"
        aria-labelledby="create-booking-form-v2__send-to-anzuk-label"
        aria-describedby="create-booking-form-v2__send-to-anzuk-description"
        class="mt-0 mr-2"
      >
        <template v-slot:label> </template>
      </v-switch>
      <div class="mt-1">
        <p class="text-md-bold mb-1">{{
          $t('bookingCreateV2.sendToANZUK.label')
        }}</p>
        <p class="text-md mb-0">{{
          $t('bookingCreateV2.sendToANZUK.description')
        }}</p>
      </div>
    </div>

    <v-text-field
      v-model="searchTerm"
      dense
      outlined
      clearable
      prepend-inner-icon="mdi-magnify"
      :placeholder="$t('bookingCreateV2.selectEducatorsStep.searchPlaceholder')"
      class="mb-lg flex-grow-0 flex-shrink-0 pr-1"
      hide-details
      :disabled="hasError || isLoading || isSendToANZUK"
      data-testid="educator-search-input"
      @keydown.enter="searchBookableEducators"
      @click:clear="clearSearch"
    />

    <div class="bookable-list p-relative rounded-lg d-flex flex-column">
      <transition name="fast-fade">
        <div
          v-if="showRemoveOverlay"
          class="remove-overlay d-flex align-center justify-center text-center"
        >
          <v-icon size="70" color="error lighten-1">mdi-delete-outline</v-icon>
        </div>
      </transition>

      <draggable
        v-if="showRemoveOverlay"
        :list="removalSink"
        :group="removeGroup"
        :sort="false"
        ghost-class="remove-target-ghost"
        class="remove-overlay-target"
        data-testid="educator-remove-overlay"
        @change="onRemoveTargetChange"
      >
        <div
          v-for="removedEducator in removalSink"
          :key="removedEducator.id"
          class="remove-target-hidden-item"
        />
      </draggable>

      <transition name="fade-up">
        <div
          v-if="displayedContentState === Error"
          key="error"
          class="bookable-content-state d-flex flex-column flex-grow-1 align-center justify-center pa-6 text-center rounded-xl"
          data-testid="educator-error-state"
        >
          <v-icon size="48" color="red lighten-1" class="mb-3"
            >mdi-alert-circle-outline</v-icon
          >
          <p class="text-sm-semibold mb-1">
            {{ $t('bookingCreateV2.selectEducatorsStep.errorTitle') }}
          </p>
          <p class="text-xs-regular neutrals-grey-500-text mb-4">
            {{ $t('bookingCreateV2.selectEducatorsStep.errorBody') }}
          </p>
          <v-btn outlined color="grey" small @click="fetchBookableEducators">
            {{ $t('bookingCreateV2.selectEducatorsStep.errorCta') }}
          </v-btn>
        </div>

        <div
          v-else-if="displayedContentState === Loading"
          key="loading"
          class="bookable-content-state d-flex flex-grow-1 align-center justify-center"
          data-testid="educator-loading-state"
        >
          <Spinner size="48" />
        </div>

        <div
          v-else-if="displayedContentState === Empty"
          key="empty"
          class="bookable-content-state d-flex flex-column flex-grow-1 align-center justify-center text-center rounded-xl mb-8"
          data-testid="educator-empty-state"
        >
          <v-icon size="32" color="grey lighten-1" class="mb-3">
            mdi-account-search-outline
          </v-icon>
          <p class="text-sm-semibold mb-1">
            {{ $t('bookingCreateV2.selectEducatorsStep.noResultsTitle') }}
          </p>
          <p class="text-xs-regular neutrals-grey-500-text mb-0 w-75">
            {{
              $t('bookingCreateV2.selectEducatorsStep.noResultsMessage', [
                $t('app.companyNameShort'),
              ])
            }}
          </p>
        </div>

        <div
          v-else-if="displayedContentState === Exhausted"
          key="exhausted"
          class="bookable-content-state d-flex flex-column flex-grow-1 align-center justify-center text-center rounded-xl mb-8"
          data-testid="educator-empty-state"
        >
          <v-icon size="48" color="grey lighten-1" class="mb-3">
            mdi-account-group-outline
          </v-icon>
          <p class="text-sm-semibold mb-1 ">
            {{ $t('bookingCreateV2.selectEducatorsStep.listExhaustedTitle') }}
          </p>
          <p class="text-xs-regular neutrals-grey-500-text mb-0 w-75">
            {{
              $t('bookingCreateV2.selectEducatorsStep.listExhaustedDescription')
            }}
          </p>
        </div>

        <div
          v-else
          key="list"
          class="bookable-content-state pr-1 overflow-y-auto bookable-scroll-container"
        >
          <draggable
            :list="visibleEducators"
            :group="availableEducatorsGroup"
            :sort="false"
            :disabled="isAvailableDragDisabled"
            filter=".drag-disabled"
            chosen-class="available-drag-chosen"
            class="fill-height"
            :class="{
              'pointer-events-none': isSendToANZUK,
              'opacity-50': isSendToANZUK || isQueueFull,
            }"
          >
            <transition-group
              tag="div"
              name="slide-fade-list"
              class="fill-height"
              @after-enter="onEducatorEnterComplete"
              @after-leave="onEducatorLeaveComplete"
            >
              <BookableEducatorCard
                v-for="educator in visibleEducators"
                :key="educator.id"
                class="mb-2"
                :educator="educator"
                @add="addEducator"
                @view-candidate="showCandidateDialog"
              />
            </transition-group>
          </draggable>
        </div>
      </transition>
    </div>

    <div class="d-flex justify-center mt-6 flex-shrink-0">
      <v-pagination
        :value="bookableEducators.currentPage"
        class="candidate-list__pagination"
        color="primary"
        :length="bookableEducators.totalPages"
        :total-visible="7"
        :disabled="!showPagination"
        data-testid="educator-pagination"
        @input="changeBookableEducatorPage"
      />
    </div>

    <CandidateDetailsModal
      v-if="showCandidateModal"
      :id="selectedCandidateId"
      v-model="showCandidateModal"
      :booking-date="bookingDate"
      :booking-time-zone="bookingTimeZone"
      @closeModal="closeCandidateDialog"
    />
  </v-card>
</template>

<style lang="scss" scoped>
@import '@/styles/_r2b-colours.scss';
@import '@/styles/_vue-transitions.scss';

.bookable-scroll-container {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
}

.bookable-content-state.fade-up-leave-active {
  position: absolute;
  inset: 0;
}

.remove-overlay {
  position: absolute;
  z-index: 4;
  pointer-events: none;
  background-color: rgba(248, 205, 211, 0.2);
  border-radius: 8px;
  outline: 2px dashed $semantics-error-300;
  outline-offset: -2px;
  inset: 0;
}

.remove-overlay-target {
  position: absolute;
  z-index: 5;
  inset: 0;
}

.remove-target-hidden-item {
  display: none;
}

::v-deep .remove-target-ghost {
  display: none;
}

::v-deep .available-drag-chosen {
  transition: none !important;
}
</style>

<style lang="scss">
@import '@/styles/candidate/candidate-list-pagination.scss';
</style>
